import {useEffect, useRef, useState} from 'react';
import {Flex, FlexProps, Image, Text, TextProps} from '@chakra-ui/react';

import {useTriviaStore} from '../state';

export function Question(props: FlexProps) {
  const question = useTriviaStore((state) => state.currentQuestion!);

  if (question.type === 'image' && question.large_image)
    return <ImageQuestion body={question.body} image={question.image} {...props} />;

  if (question.type === 'image')
    return <FloatingImageQuestion body={question.body} image={question.image} {...props} />;

  return (
    <Flex {...props}>
      <QuestionText flex={1}>{question.body}</QuestionText>
    </Flex>
  );
}

interface ImageQuestionProps extends FlexProps {
  body: string;
  image: string;
}

const FloatingImageQuestion = ({body, image, ...props}: ImageQuestionProps) => {
  const questionImageRef = useRef<HTMLImageElement>(null);
  const [gap, setGap] = useState(0);

  useEffect(() => {
    const questionImageRadius = (questionImageRef.current?.getBoundingClientRect().height ?? 0) / 2;

    setGap(questionImageRadius);
  }, [questionImageRef, image]);

  return (
    <Flex pos="relative" flexDir="column" isolation="isolate" {...props} mt={`calc(${+(props.mt ?? 0) * 4 + gap}px)`}>
      <Image
        alignSelf="center"
        ref={questionImageRef}
        src={image}
        alt=""
        pos="absolute"
        w="50%"
        sx={{aspectRatio: '1/1'}}
        minW="36"
        maxW="56"
        objectFit="cover"
        rounded="full"
        top="0"
        transform="auto"
        translateY="-50%"
      />

      <QuestionText mt={{base: 4, lg: 5}} pt={`${gap}px`}>
        {body}
      </QuestionText>
    </Flex>
  );
};

const ImageQuestion = ({body, image, ...props}: ImageQuestionProps) => (
  <Flex flexDir="column" gap="2" {...props}>
    <Image h={[60, 64, 72, 80]} w="auto" objectFit="contain" src={image} alt="" />

    <QuestionText pt="4" pb="4" rounded="3xl">
      {body}
    </QuestionText>
  </Flex>
);

const QuestionText = (props: TextProps) => (
  <Text
    as="h2"
    bg="#efefef"
    align="center"
    fontSize={{base: 'lg', lg: 'xl'}}
    fontWeight="700"
    px={{base: 6, lg: 10}}
    pt={{base: 12, lg: 8}}
    pb={{base: 12, lg: 8}}
    rounded="2rem"
    {...props}
  />
);
